import { useCallback, useEffect, useState } from 'react';
import { Page, BlockStack, InlineStack, Button, Banner, Badge, Toast, Text, Spinner, Box } from '@shopify/polaris';
import { TabTitle } from '../../../shell/helmet.jsx';
import { useAuthStore } from '../../../context/useStore.js';
import userRoles from '../../../utils/userRoles.js';
import EstimateTab from './EstimateTab.jsx';
import SavedEstimatesTab from './SavedEstimatesTab.jsx';
import PriceBookTab from './PriceBookTab.jsx';
import TrainingTab from './TrainingTab.jsx';
import { api, formatDate } from './shared.js';

/*
  Reports > Cabinet price estimator: what will Elektro Tech charge for these control cabinets?
  Server: /api/reports/cabinet-estimator (server/routes/cabinet_estimator.js). Panels stay mounted (display none) so the
  estimate form keeps its state when switching tabs.
*/
export default function CabinetEstimatorPage() {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === userRoles.AdminFull;
  const [tab, setTab] = useState('estimate');
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const [loadedEstimate, setLoadedEstimate] = useState(null);
  const [savedVersion, setSavedVersion] = useState(0);
  const [toast, setToast] = useState(null);

  const notify = useCallback((content, isError = false) => setToast({ content, error: isError }), []);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await api('/'));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const tabs = [
    { id: 'estimate', label: 'Estimate' },
    { id: 'saved', label: 'Saved estimates' },
    { id: 'prices', label: 'Price book' },
    ...(isSuperAdmin && overview?.canTrain ? [{ id: 'training', label: 'Training' }] : [])
  ];

  const model = overview?.model;
  const subtitle = overview
    ? model?.trained
      ? `${overview.supplier?.name || 'Elektro Tech'} price level ${formatDate(model.referenceDate)} · learned from ${model.metrics.sources} documents`
      : `${overview.supplier?.name || 'Elektro Tech'} · built-in 2026 prices (not trained yet)`
    : 'Loading…';

  return (
    <Page
      title="Cabinet price estimator"
      subtitle={subtitle}
      backAction={{ content: 'Reports', url: '/reports' }}
      titleMetadata={model ? model.trained ? <Badge tone="success">{`Model #${model.id}`}</Badge> : <Badge tone="attention">Default prices</Badge> : null}
      fullWidth
    >
      <TabTitle title="Cabinet price estimator" />
      <BlockStack gap="400">
        {error && <Banner tone="critical">{error}</Banner>}
        {overview && !overview.supplier && <Banner tone="warning">Supplier Elektro Tech (customer number 06963) was not found in this organisation: estimates use the built-in prices.</Banner>}
        {overview && !overview.tablesAvailable && isSuperAdmin && (
          <Banner tone="warning" title="Database migration needed">
            Run server/sql/create_cabinet_estimator_tables.sql on this database to train the model and save estimates. Estimating works without it.
          </Banner>
        )}
        <InlineStack gap="200" wrap>
          {tabs.map((t) => (
            <Button key={t.id} variant="tertiary" pressed={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </Button>
          ))}
        </InlineStack>
        {!overview && !error ? (
          <Box padding="800">
            <InlineStack align="center">
              <Spinner accessibilityLabel="Loading" />
            </InlineStack>
          </Box>
        ) : (
          overview && (
            <>
              <div style={{ display: tab === 'estimate' ? 'block' : 'none' }}>
                <EstimateTab overview={overview} loaded={loadedEstimate} notify={notify} onSaved={() => setSavedVersion((v) => v + 1)} />
              </div>
              <div style={{ display: tab === 'saved' ? 'block' : 'none' }}>
                <SavedEstimatesTab
                  refreshKey={savedVersion}
                  tablesAvailable={overview.tablesAvailable}
                  notify={notify}
                  onOpen={(estimate) => {
                    setLoadedEstimate(estimate);
                    setTab('estimate');
                  }}
                />
              </div>
              <div style={{ display: tab === 'prices' ? 'block' : 'none' }}>
                <PriceBookTab key={model?.id || 'prior'} />
              </div>
              {isSuperAdmin && overview.canTrain && (
                <div style={{ display: tab === 'training' ? 'block' : 'none' }}>
                  {overview.tablesAvailable ? (
                    <TrainingTab overview={overview} notify={notify} onModelChanged={loadOverview} />
                  ) : (
                    <Text as="p" tone="subdued">
                      Training needs the database migration.
                    </Text>
                  )}
                </div>
              )}
            </>
          )
        )}
      </BlockStack>
      {toast && <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />}
    </Page>
  );
}
